import React, { useState, useEffect } from 'react';

import { CaseInfoCard } from '../../components/common/CaseInfoCard';
import { TabNavigation, TabType, PerformanceFilterType } from './TabNavigation';
import { CaseReport, CaseReportSummary } from '../../models/CaseTypes';
import ICaseReportService from '../../services/ICaseReportService';
import { PerformanceSection } from './PerformanceSection';
import { KeyMomentsSection } from './KeyMomentsSection';
import { CaseRecordingSection } from './CaseRecordingSection';
import { Message } from '../../components/common/Message';

interface CaseReportPageProps {
  caseName: string;
  caseReportService: ICaseReportService;
  defaultTab?: TabType;
  onNavigate: (page: string) => void;
}

export function CaseReportPage(
  { caseName, caseReportService, defaultTab, onNavigate }: CaseReportPageProps
): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<TabType>(defaultTab || 'performance');
  const [performanceFilter, setPerformanceFilter] = useState<PerformanceFilterType>('TEAM');
  const [caseSummaries, setCaseSummaries] = useState<CaseReportSummary[] | null>(null);
  const [caseReport, setCaseReport] = useState<CaseReport | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Attempt to load specified case by name.
  useEffect(() => {

    const fetchCase = async (): Promise<void> => {
      try {
        setIsLoading(true);
        setError('');

        // Retrieve summaries in order to find case by shortened name:
        let summaries = caseSummaries;
        if (!summaries) {
          summaries = await caseReportService.getCaseSummaries();
          setCaseSummaries(summaries);
        }
        const caseId = summaries?.find(c => c.name === caseName)?.id;
        if (!caseId) {
          return onNavigate('/cases/library');
        }

        const caseDetail = await caseReportService.getCaseDetail(caseId);
        if (!caseDetail) {
          console.error('Case detail is null or undefined');
          setError('Unable to load case report.');
        } else {
          setCaseReport(caseDetail);
        }

      } catch (error) {
        console.error('Error fetching case detail:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCase();
  }, [caseName, caseReportService, onNavigate, caseSummaries]);

  // Default to 'case-recording' tab for non-navigated cases
  useEffect(() => {
    if (caseReport && !caseReport.hasActivities) {
      setActiveTab('case-recording');
    }
  }, [caseReport]);

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1 w-full max-w-[1440px] mx-auto px-[20px] lg:px-5 py-4 lg:py-5">
        <div className="flex flex-col gap-4 lg:gap-5">
          { isLoading && (
            <p>Loading case report...</p>
          )}

          { error && (
            <p className="text-danger">Error loading case report: {error}</p>
          )}

          {/* TODO Analytics Toast
          <AnalyticsToast
            activeTab={activeTab}
            onNavigate={onNavigate}
          />
          */}

          {/* Performance Unavailable Notice - shows only on case-recording tab */}
          {activeTab === 'case-recording' && caseReport && !caseReport.hasActivities && (
            <Message
              type="warning"
              message="Performance analytics are unavailable for non-navigated Paradigm cases."
            />
          )}

          {/* Page Title */}
          <h1>Case Report</h1>

          {/* Case Information Card */}
          {
            caseReport &&
            <CaseInfoCard caseReport={caseReport}/>
          }

          {/* Section Title */}
          <h3>Case Details</h3>

          {/* Tab Navigation */}
          <TabNavigation
            activeTab={activeTab}
            onTabChange={setActiveTab}
            performanceFilter={performanceFilter}
            onPerformanceFilterChange={setPerformanceFilter}
            disabledTabs={caseReport && !caseReport.hasActivities ? ['performance'] : []}
          />

          {/* Tab Content */}
          {activeTab === 'case-recording' && caseReport && (
            <div
              role="tabpanel"
              id="tabpanel-case-recording"
              aria-labelledby="tab-case-recording"
            >
              <CaseRecordingSection caseReport={caseReport} />
            </div>
          )}

          {activeTab === 'performance' && caseReport && (
            <div
              role="tabpanel"
              id="tabpanel-performance"
              aria-labelledby="tab-performance"
            >
              <PerformanceSection caseReport={caseReport} performanceFilter={performanceFilter} />
            </div>
          )}

          {activeTab === 'key-moments' && caseReport && (
            <div
              role="tabpanel"
              id="tabpanel-key-moments"
              aria-labelledby="tab-key-moments"
            >
              <KeyMomentsSection caseReport={caseReport} />
            </div>
          )}

          {/* TODO Post Op CT Validation Image Toast
          <PostOpCTValidationImageToast
            activeTab={activeTab}
            onNavigate={onNavigate}
          />
          */}
        </div>
      </main>
    </div>
  );
}
